import { type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/base/popover';

/**
 * Catálogo central de siglas e termos técnicos do TEG, cópia do protótipo
 * (`GlossaryTerm.tsx`, referência estrutural fora do git deste repo).
 *
 * Use sempre este catálogo — não duplique definição de termo em outro lugar.
 * Para adicionar um termo novo, inclua a chave em `GLOSSARIO` abaixo.
 */
export const GLOSSARIO = {
  DAMSP: {
    titulo: 'DAMSP — Documento de Arrecadação Municipal de São Paulo',
    corpo: 'Boleto/guia emitido pela Prefeitura para cobrança das taxas devidas pelo credenciado, inclusive em substituições de condutor/veículo. Enquanto não paga, ações operacionais dependentes podem ficar bloqueadas.',
  },
  ADITIVO: {
    titulo: 'Aditivo Contratual',
    corpo: 'Documento que altera condições de um Termo de Adesão vigente (prazos, recursos, valores). Herda a numeração SEI do termo de origem.',
  },
  REVISAO_OS: {
    titulo: 'Revisão A / B / C de OS',
    corpo: 'Toda substituição de condutor, monitor ou veículo gera automaticamente uma nova versão (revisão) da Ordem de Serviço, identificada por uma letra sequencial (A, B, C…). A revisão substitui a anterior na operação.',
  },
  DTEG: {
    titulo: 'DTEG — Diretoria de Transporte Escolar Gratuito',
    corpo: 'Área da Prefeitura responsável pela aprovação técnica e fiscalização do credenciamento e da operação do TEG.',
  },
  DTP: {
    titulo: 'DTP — Departamento de Transportes Públicos',
    corpo: 'Equipe administrativa/operacional que conduz o dia a dia do TEG: ordens de serviço, cadastros, demandas e atendimento.',
  },
  PJ: { titulo: 'PJ — Pessoa Jurídica', corpo: 'Credenciado pessoa jurídica (empresa ou cooperativa).' },
  PF: { titulo: 'PF — Pessoa Física', corpo: 'Credenciado pessoa física (autônomo).' },
  COOP: {
    titulo: 'COOP — Cooperativa',
    corpo: 'Credenciado pessoa jurídica do tipo cooperativa, com regras próprias de vínculo e demonstrativo.',
  },
  SLA: {
    titulo: 'SLA — Service Level Agreement',
    corpo: 'Prazo-alvo para conclusão de uma ação ou etapa. Itens com SLA estourado aparecem em destaque.',
  },
  P90: {
    titulo: 'P90 — Percentil 90',
    corpo: '90% dos casos são resolvidos em até este tempo; apenas 10% demoram mais. Usado para medir o pior cenário típico, complementando a mediana.',
  },
  DIAS_UTEIS: {
    titulo: 'du — Dias úteis',
    corpo: 'Conta apenas dias de expediente (segunda a sexta), excluindo finais de semana e feriados. Reflete melhor a capacidade real da equipe.',
  },
  FILA_VIVA: {
    titulo: 'Fila viva',
    corpo: 'Inscrições que ainda não foram decididas (aprovadas ou reprovadas) e continuam aguardando análise da DTP neste momento.',
  },
  COMPETENCIA: {
    titulo: 'Competência',
    corpo: 'Mês de referência (MM/AAAA) ao qual os movimentos financeiros pertencem, independentemente de quando foram lançados ou pagos. É a unidade básica de fechamento mensal do TEG.',
  },
  ATIV_COMPLEMENTAR: {
    titulo: 'Atividade Complementar',
    corpo: 'Dia em que o condutor não realizou a rota regular mas executou atividade prevista em edital (presencial, remoto ou externa). Conta para remuneração e precisa de registro com data, escola e tipo.',
  },
  SELAMENTO: {
    titulo: 'Selamento Mensal',
    corpo: 'Ato de fechar a competência: após selada, os movimentos do mês ficam imutáveis e seguem para medição → demonstrativo → pagamento. Reabertura exige justificativa formal.',
  },
  MEDICAO: {
    titulo: 'Medição',
    corpo: 'Documento que consolida os movimentos selados de uma competência por credenciado e gera o valor a pagar. É a base para o demonstrativo financeiro e a saída fiscal (SOF).',
  },
  DIAS_OPERADOS: {
    titulo: 'Dias Operados',
    corpo: 'Quantidade de dias letivos em que a OS efetivamente rodou no mês. A diferença em relação aos dias úteis vira Faltas e impacta o valor a remunerar.',
  },
  AJUSTE_VALOR: {
    titulo: 'Ajuste manual',
    corpo: 'Acréscimo (positivo) ou desconto (negativo) aplicado pelo financeiro ao movimento do mês. Sempre exige justificativa registrada e fica auditável.',
  },
  LEAD_TIME: {
    titulo: 'Lead time financeiro',
    corpo: 'Tempo médio (em dias corridos) entre a aprovação da medição e o pagamento confirmado em conciliação bancária. Mede a eficiência ponta-a-ponta do ciclo financeiro.',
  },
  CONCILIACAO: {
    titulo: 'Conciliação bancária',
    corpo: "Cruzamento entre demonstrativos pagos e o extrato bancário recebido. Itens 'pendentes' foram marcados como pagos no sistema mas ainda não bateram com nenhuma linha do extrato.",
  },
  PIPELINE_PAGAMENTO: {
    titulo: 'Pipeline de pagamento',
    corpo: 'Percentual do valor já distribuído aos credenciados que também já foi encaminhado ao SOF em lote de empenho. Quanto mais alto, menor o estoque parado entre distribuição e pagamento.',
  },
  CUMPRIMENTO_PRAZOS: {
    titulo: 'Cumprimento de prazos',
    corpo: 'Percentual de prazos institucionais (calendário legal) entregues dentro da data-alvo. Vencidos contam como descumprimento; em risco ainda podem ser salvos.',
  },
  INTEGRIDADE_AUDIT: {
    titulo: 'Integridade da auditoria',
    corpo: "Verificação da cadeia SHA-256 do log financeiro imutável. 'Íntegra' significa que nenhum evento foi alterado ou removido após gravação. Quebras devem ser investigadas imediatamente.",
  },
  GLOSAS: {
    titulo: 'Glosas / devoluções',
    corpo: 'Demonstrativos recusados pelo credenciado ou pela auditoria interna no período. Valores precisam ser corrigidos e reapresentados antes do pagamento.',
  },
  EMPENHO: {
    titulo: 'Empenho (NE)',
    corpo: 'Reserva orçamentária formal feita pela Prefeitura para garantir o pagamento de uma medição. Identificado por número de Nota de Empenho (NE) e dotação orçamentária. Sem empenho não há liquidação nem pagamento.',
  },
  PRE_EMPENHO: {
    titulo: 'Pré-empenho',
    corpo: 'Reserva preliminar enviada ao SOF antes da geração formal da NE. Confirma a disponibilidade orçamentária mas ainda pode ser ajustado ou anulado.',
  },
  LOTE_SOF: {
    titulo: 'Lote SOF',
    corpo: 'Pacote de medições aprovadas exportado em um único arquivo para o Sistema de Orçamento e Finanças (SOF) da Prefeitura. Cada lote vira um conjunto de NEs e segue para liquidação e pagamento.',
  },
  AJUSTE_BONUS: {
    titulo: 'Ajustes (bônus / glosa)',
    corpo: 'Acréscimos (bônus) ou descontos (glosa, multa avulsa) aplicados manualmente sobre uma medição antes da exportação SOF. Sempre exigem justificativa e ficam auditáveis. Após a medição ser exportada ou paga, novos ajustes ficam bloqueados.',
  },
  SLA_APROVACAO_SME: {
    titulo: 'SLA de aprovação SME',
    corpo: 'Prazo-alvo de 5 dias úteis para a SME analisar (aprovar ou devolver) cada lote enviado pelas DREs. Lotes vencidos bloqueiam o ciclo financeiro do credenciado e disparam escalonamento automático.',
  },
  APONTAMENTO: {
    titulo: 'Apontamento',
    corpo: 'Registro diário do que ocorreu na rota: dias operados, faltas, atividades complementares e número de alunos. É a base para a medição mensal e o pagamento ao credenciado.',
  },
  COBERTURA_DRE: {
    titulo: 'Cobertura por DRE',
    corpo: 'Percentual de credenciados ativos da DRE com apontamento entregue (rascunho ou enviado) no mês. Coberturas baixas indicam DREs em atraso operacional.',
  },
  DEMANDA_SME: {
    titulo: 'Demanda SME → DTP',
    corpo: 'Solicitação formal feita pela Secretaria Municipal de Educação (ou por uma DRE) para a equipe de transporte (DTP) executar uma alteração operacional: nova rota, troca de veículo/condutor, inclusão de aluno etc. Cada demanda tem ciclo próprio (Aberta → Em análise → Atendida/Recusada) e é a porta de entrada para mudanças não originadas pelo próprio credenciado.',
  },
  SLA_DEMANDA: {
    titulo: 'SLA da demanda',
    corpo: 'Prazo desejado informado pela SME para conclusão da solicitação. Demandas com SLA estourado ficam destacadas em vermelho e devem ser priorizadas pela DTP; vencendo nas próximas 48 h aparecem em amarelo.',
  },
  LOTE_APONTAMENTO: {
    titulo: 'Lote de apontamento',
    corpo: 'Conjunto de dias operados de um credenciado em uma competência, agrupado pela DRE para envio único à SME. Cada lote tem status próprio (Rascunho → Enviado → Aprovado/Devolvido) e é a unidade base de aprovação financeira.',
  },
  REVISAO_MEDICAO: {
    titulo: 'Revisão de medição',
    corpo: 'Pedido formal de reanálise sobre uma medição já consolidada. Pode ser aberto pela DRE ou pelo credenciado quando houver divergência de valores, faltas ou ajustes. Bloqueia o pagamento até decisão da SME.',
  },
  DRE_DEFASADA: {
    titulo: 'DRE em defasagem',
    corpo: 'DRE com lotes parados há mais de 7 dias sem ação (envio, devolução ou complementação). Indica gargalo operacional que precisa ser cobrado pela SME para destravar o ciclo.',
  },
  OCORRENCIA: {
    titulo: 'Ocorrência de fiscalização',
    corpo: 'Registro de fato relevante observado na operação (atraso, ausência, condutor irregular, acidente, reclamação etc.). É a porta de entrada da fiscalização: dependendo da apuração, pode evoluir para auto de infração e, então, multa.',
  },
  AUTO_INFRACAO: {
    titulo: 'Auto de infração',
    corpo: 'Documento formal lavrado pelo fiscal quando a ocorrência é confirmada como infração ao edital/regulamento. É a base legal para a aplicação de multa e pode ser contestado pelo credenciado via recurso.',
  },
  GRAVIDADE: {
    titulo: 'Gravidade da ocorrência',
    corpo: 'Classificação de impacto: Leve (advertência), Média (multa proporcional), Grave (multa cheia + suspensão temporária) e Gravíssima (multa máxima + risco de descredenciamento). Determina o rito de apuração e o prazo de resposta.',
  },
  SLA_FISCALIZACAO: {
    titulo: 'SLA de fiscalização',
    corpo: 'Prazo-alvo de 15 dias corridos para concluir a apuração de uma ocorrência (resolver ou arquivar). Ocorrências abertas há mais tempo aparecem em destaque vermelho e devem ser priorizadas.',
  },
  MULTA: {
    titulo: 'Multa',
    corpo: 'Penalidade financeira aplicada após confirmação de infração. É calculada como um percentual sobre o valor-base do credenciado no mês de referência e, quando aplicada, deve ser descontada na próxima medição. Pode ser cancelada antes do desconto via decisão da DTP ou recurso deferido.',
  },
  PERCENTUAL_INFRACAO: {
    titulo: 'Percentual da infração',
    corpo: 'Alíquota aplicada sobre o valor-base do mês para chegar ao valor da multa. Definido pelo edital conforme a gravidade: leve (até 1%), média (1–3%), grave (3–5%), gravíssima (acima de 5%, com possível suspensão).',
  },
  DESCONTO_MEDICAO: {
    titulo: 'Desconto em medição',
    corpo: "Quando uma multa é aplicada, ela fica vinculada a uma medição mensal e é descontada do valor a pagar do credenciado. Multas marcadas como 'Pendente' ainda não foram amarradas a nenhuma medição e seguem em aberto.",
  },
  MULTA_PARADA: {
    titulo: 'Multa parada',
    corpo: 'Multa pendente cujo mês de referência já passou de 30 dias sem ser amarrada a uma medição (vencendo) ou de 60 dias (vencida). Indica gargalo de fechamento financeiro: o valor não está sendo descontado e o crédito do credenciado não foi ajustado.',
  },
  RECURSO: {
    titulo: 'Recurso administrativo',
    corpo: 'Manifestação formal de inconformidade do credenciado contra atos do TEG (indeferimento de inscrição, cobrança DAMSP, multa, etc.). Tem prazo legal de resposta e segue rito administrativo com possibilidade de 1ª e 2ª instância.',
  },
  INSTANCIA_RECURSAL: {
    titulo: 'Instância recursal',
    corpo: '1ª instância é a primeira análise pela equipe operacional do TEG; 2ª instância é o reexame por colegiado superior, quando o credenciado discorda da decisão de 1ª instância.',
  },
  PRAZO_RESPOSTA: {
    titulo: 'Prazo de resposta',
    corpo: 'Data limite para a Administração decidir o recurso (deferir/indeferir). O descumprimento gera risco jurídico e processual. Recursos com prazo vencido devem ser priorizados imediatamente.',
  },
  DEFERIDO_INDEFERIDO: {
    titulo: 'Deferido / Indeferido',
    corpo: 'Deferido = recurso aceito (revoga ou modifica o ato contestado). Indeferido = recurso rejeitado (mantém o ato original). Ambas as decisões encerram a instância e devem ser fundamentadas.',
  },
  HASH_ENCADEADO: {
    titulo: 'Hash encadeado (SHA-256)',
    corpo: 'Cada evento da trilha de auditoria gera um hash SHA-256 que inclui o hash do evento anterior. Qualquer adulteração quebra a cadeia e é detectada pela verificação de integridade. Garante que o histórico financeiro é imutável.',
  },
  SEQUENCIA_AUDIT: {
    titulo: 'Sequência (#) — trilha imutável',
    corpo: 'Número incremental que identifica a ordem global dos eventos auditados. É a posição do evento na cadeia: a sequência #1 é o GENESIS e cada novo evento recebe o próximo número. Saltos ou repetições indicam adulteração.',
  },
  CATEGORIA_AUDIT: {
    titulo: 'Categoria de auditoria',
    corpo: 'Agrupamento da operação financeira: FECHAMENTO (selamento de competência), DEMONSTRATIVO (geração/aprovação de demonstrativo), LOTE_SOF (envio de lote ao SOF) e PRAZO_LEGAL (movimentação em prazos regulatórios).',
  },
  PARECER: {
    titulo: 'Parecer',
    corpo: 'Justificativa formal registrada pela equipe DTP ao decidir a inscrição. Em caso de reprovação ou pendência, descreve com clareza o motivo e o que precisa ser corrigido.',
  },
  PRAZO_CORRECAO: {
    titulo: 'Prazo de correção',
    corpo: 'Data limite para reenvio dos documentos ou dados solicitados pela DTP. Se vencer sem ação, a inscrição é automaticamente expirada — exigindo reabertura ou novo cadastro.',
  },
  LOTE_SME: {
    titulo: 'Lote SME',
    corpo: 'Conjunto de OSs de uma DRE, separadas por PF/PJ, aprovado ou devolvido pela SME em uma única decisão. Aprovado em bloco pela chave mês × DRE × tipo de pessoa — nunca OS por OS.',
  },
  REMESSA_MENSAL: {
    titulo: 'Remessa mensal',
    corpo: 'Pacote oficial enviado pela DRE à SME no fechamento do mês — uma remessa por mês.',
  },
} as const;

export type ChaveDoGlossario = keyof typeof GLOSSARIO;

interface GlossaryTermProps {
  /** Chave do termo no catálogo `GLOSSARIO`. */
  termo: ChaveDoGlossario;
  /** Texto exibido (padrão: a própria chave). */
  children?: ReactNode;
  className?: string;
  /** Esconde o ícone `?` ao lado (mantém só o sublinhado). */
  esconderIcone?: boolean;
}

/**
 * Termo técnico com glossário num Popover acessível: sublinhado pontilhado +
 * ícone `?`; ao clicar ou focar, abre com título e descrição em português.
 * 100% navegável por teclado (Tab → Enter/Espaço).
 */
export function GlossaryTerm({ termo, children, className, esconderIcone }: GlossaryTermProps) {
  const definicao = GLOSSARIO[termo];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Definição: ${definicao.titulo}`}
          className={cn(
            'inline-flex items-center gap-1 underline decoration-dotted underline-offset-2',
            'text-inherit hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-sm',
            className,
          )}
        >
          <span>{children ?? termo}</span>
          {!esconderIcone && <HelpCircle className="h-3 w-3 opacity-60" aria-hidden />}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="max-w-xs text-sm">
        <p className="mb-1 font-semibold">{definicao.titulo}</p>
        <p className="text-muted-foreground">{definicao.corpo}</p>
      </PopoverContent>
    </Popover>
  );
}

export default GlossaryTerm;
